import { useCallback, useMemo, useRef } from 'react';
import { cancelSpeech, speakDutch } from '../utils/tts';

interface SpeakOptions {
  /** Pace of the visible caption, per word */
  wordMs: number;
  volume?: number;
  /** Called as each word is revealed (1-based count) */
  onWord: (count: number) => void;
  /** Called once the caption has played out AND the voice has finished (or timed out) */
  onDone: () => void;
}

/**
 * Anna saying one line: the caption reveals word by word while the voice speaks.
 * Both must finish before she is "done"; a newer line or a barge-in cancels the older one.
 */
export function useSpokenLine(later: (fn: () => void, ms: number) => void, voiceOn: () => boolean) {
  const current = useRef(0);

  const speak = useCallback(
    (text: string, { wordMs, volume = 1, onWord, onDone }: SpeakOptions) => {
      const id = ++current.current;
      const words = text.split(' ');
      words.forEach((_, i) => later(() => current.current === id && onWord(i + 1), i * wordMs));

      let timerDone = false;
      let ttsDone = !voiceOn();
      const finish = () => {
        if (timerDone && ttsDone && current.current === id) onDone();
      };
      const minEnd = words.length * wordMs + 300;
      later(() => {
        timerDone = true;
        finish();
      }, minEnd);
      // Safety net if the browser never reports the end of speech.
      later(() => {
        ttsDone = true;
        finish();
      }, minEnd + 8000);
      if (voiceOn()) {
        const ok = speakDutch(text, {
          volume,
          onEnd: () => {
            ttsDone = true;
            finish();
          },
        });
        if (!ok) ttsDone = true;
      }
      return id;
    },
    [later, voiceOn],
  );

  const cancel = useCallback(() => {
    current.current++;
    cancelSpeech();
  }, []);

  const isCurrent = useCallback((id: number) => current.current === id, []);

  // Stable identity, so directors can list it as a dependency without re-running effects.
  return useMemo(() => ({ speak, cancel, isCurrent }), [speak, cancel, isCurrent]);
}
